import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from './Toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const EmailAdmin = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('templates');
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [sesStatus, setSesStatus] = useState(null);

  useEffect(() => {
    loadEmailData();
  }, []);

  const loadEmailData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      // Load email templates
      const templatesResponse = await axios.get(`${API_BASE}/api/email/templates`, { headers });
      setEmailTemplates(templatesResponse.data || []);

      // Load SES status
      try {
        const sesResponse = await axios.get(`${API_BASE}/api/email/ses-status`, { headers });
        setSesStatus(sesResponse.data);
      } catch (err) {
        console.log('SES status not available:', err);
      }

    } catch (error) {
      console.error('Failed to load email data:', error);
      toast.error('Kunne ikke laste email data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({
      ...template,
      subject: String(template.subject || ''),
      content: String(template.content || ''),
      name: String(template.name || '')
    });
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;

    try {
      const headers = { 
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      await axios.put(
        `${API_BASE}/api/email/templates/${editingTemplate.id}`, 
        {
          subject: editingTemplate.subject,
          content: editingTemplate.content,
          name: editingTemplate.name
        },
        { headers }
      );

      toast.success('Email mal oppdatert');
      setEditingTemplate(null);
      loadEmailData();
    } catch (error) {
      console.error('Save template error:', error);
      toast.error('Kunne ikke lagre email mal');
    }
  };

  const renderTemplatePreview = (content) => {
    if (!content) return 'Ingen innhold';
    
    const safeContent = String(content)
      .replace(/\{\{organizationName\}\}/g, 'Eksempel Pistolklubb')
      .replace(/\{\{recipientName\}\}/g, 'Eksempel Bruker')
      .replace(/\{\{email\}\}/g, 'bruker@eksempel.no')
      .replace(/\{\{([^}]+)\}\}/g, '[Variabel: $1]');
    
    return safeContent.length > 100 ? safeContent.substring(0, 100) + '...' : safeContent;
  };

  const handlePreviewTemplate = (template) => {
    const previewContent = String(template.content || '')
      .replace(/\{\{organizationName\}\}/g, 'Eksempel Pistolklubb')
      .replace(/\{\{recipientName\}\}/g, 'Eksempel Bruker')
      .replace(/\{\{email\}\}/g, 'bruker@eksempel.no')
      .replace(/\{\{memberNumber\}\}/g, '12345')
      .replace(/\{\{loginUrl\}\}/g, process.env.REACT_APP_FRONTEND_URL || window.location.origin)
      .replace(/\{\{adminName\}\}/g, 'Admin Eksempel')
      .replace(/\{\{password\}\}/g, 'DittPassord123')
      .replace(/\{\{currentMembers\}\}/g, '15')
      .replace(/\{\{maxMembers\}\}/g, '20')
      .replace(/\{\{utilizationPercent\}\}/g, '75.0')
      .replace(/\{\{actionRequired\}\}/g, 'Vurder å oppgradere ditt abonnement for å støtte flere medlemmer.')
      .replace(/\{\{primary_color\}\}/g, '#FFD700')
      .replace(/\{\{secondary_color\}\}/g, '#1E2330')
      .replace(/\{\{accent_color\}\}/g, '#FF6B35')
      .replace(/\{\{([^}]+)\}\}/g, '[Variabel: $1]');

    const previewSubject = String(template.subject || '')
      .replace(/\{\{organizationName\}\}/g, 'Eksempel Pistolklubb')
      .replace(/\{\{([^}]+)\}\}/g, '[Variabel: $1]');

    const previewWindow = window.open('', '_blank', 'width=600,height=800,scrollbars=yes,resizable=yes');
    
    if (previewWindow) {
      // Add line breaks for better readability
      const formattedContent = previewContent
        .replace(/\n/g, '<br>')
        .replace(/\. /g, '.<br><br>')
        .replace(/! /g, '!<br><br>')
        .replace(/\? /g, '?<br><br>');

      previewWindow.document.write(`
        <!DOCTYPE html>
        <html lang="no">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=yes">
          <title>📧 ${String(template.name || 'E-post mal')}</title>
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; 
              margin: 0; 
              padding: 0;
              background-color: #f8f9fa;
              font-size: 16px;
              line-height: 1.6;
              zoom: 1;
              transform-origin: top left;
            }
            .container {
              max-width: 600px;
              margin: 0 auto;
              background: white;
              box-shadow: 0 4px 12px rgba(0,0,0,0.15);
              border-radius: 8px;
              overflow: hidden;
            }
            .header { 
              background: linear-gradient(135deg, #1E2330 0%, #374151 100%); 
              color: white; 
              padding: 20px; 
              text-align: center;
              position: relative;
            }
            .logo {
              width: 48px;
              height: 48px;
              background: #FFD700;
              border-radius: 8px;
              margin: 0 auto 16px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 24px;
              font-weight: bold;
              color: #1E2330;
            }
            .header h1 {
              font-size: 24px;
              margin: 0 0 8px 0;
              color: #FFD700;
              font-weight: bold;
            }
            .header p {
              font-size: 16px;
              margin: 4px 0;
              opacity: 0.9;
              color: #e5e7eb;
            }
            .email-body { 
              padding: 24px; 
              line-height: 1.7;
              font-size: 16px;
              background: white;
            }
            .preview-note {
              background: linear-gradient(90deg, #FFD700, #fbbf24);
              color: #1E2330;
              padding: 16px;
              margin-bottom: 24px;
              border-radius: 8px;
              font-size: 14px;
              font-weight: 500;
              border: 2px solid #f59e0b;
            }
            .email-content {
              font-size: 16px;
              line-height: 1.7;
              color: #333;
            }
            .email-content p {
              margin: 0 0 16px 0;
            }
            .email-content h1, .email-content h2, .email-content h3 {
              margin: 24px 0 12px 0;
              color: #1E2330;
            }
            .email-content h1 {
              color: #FFD700;
              border-bottom: 2px solid #FFD700;
              padding-bottom: 8px;
            }
            .email-content h2 {
              color: #1E2330;
              border-bottom: 1px solid #e5e7eb;
              padding-bottom: 4px;
            }
            .email-content ul, .email-content ol {
              margin: 16px 0;
              padding-left: 20px;
            }
            .email-content li {
              margin: 8px 0;
            }
            .email-content a {
              color: #FFD700;
              text-decoration: none;
            }
            .email-content a:hover {
              text-decoration: underline;
            }
            .close-btn {
              position: fixed;
              top: 15px;
              right: 15px;
              background: #1E2330;
              color: white;
              border: none;
              border-radius: 50%;
              width: 50px;
              height: 50px;
              font-size: 24px;
              cursor: pointer;
              box-shadow: 0 4px 12px rgba(30, 35, 48, 0.3);
              z-index: 1000;
              transition: all 0.2s;
            }
            .close-btn:hover {
              background: #FFD700;
              color: #1E2330;
              transform: scale(1.05);
            }
            .footer {
              background: #f9fafb;
              padding: 20px;
              text-align: center;
              border-top: 1px solid #e5e7eb;
              color: #6b7280;
              font-size: 14px;
            }
            @media (max-width: 480px) {
              .email-body { padding: 20px; }
              .header { padding: 16px; }
              .header h1 { font-size: 20px; }
              .email-content { font-size: 15px; }
              .container { margin: 10px; max-width: calc(100% - 20px); }
            }
          </style>
        </head>
        <body>
          <button class="close-btn" onclick="window.close()" title="Lukk forhåndsvisning">&times;</button>
          <div class="container">
            <div class="header">
              <div style="margin-bottom: 16px;">
                <img src="https://customer-assets.emergentagent.com/job_training-logbook/artifacts/yey6rh52_Aktivlogg-logo-invertert-1linje.png" 
                     alt="Aktivlogg" 
                     style="height: 32px; width: auto; max-width: 200px; object-fit: contain;"
                     onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
                <div style="display: none; color: #FFD700; font-size: 24px; font-weight: bold;">Aktivlogg</div>
              </div>
              <p>Digital treningslogg for pistolklubber</p>
              <div style="margin-top: 16px; padding: 8px 16px; background: rgba(255, 215, 0, 0.2); border-radius: 20px; display: inline-block;">
                <strong style="color: #FFD700;">Emne:</strong> ${previewSubject}
              </div>
            </div>
            <div class="email-body">
              <div class="preview-note">
                <strong>📋 FORHÅNDSVISNING:</strong> Slik vil e-posten se ut for mottakerne med eksempeldata.
                <br><strong>Mal:</strong> ${String(template.name || 'Ukjent')}
              </div>
              <div class="email-content">
                ${formattedContent}
              </div>
            </div>
            <div class="footer">
              <p><strong>Aktivlogg</strong> - Digital treningslogg for pistolklubber og skytterlag</p>
              <p style="margin: 8px 0 0 0; font-size: 12px;">
                <a href="https://aktivlogg.no/personvernerklaering-2" target="_blank" rel="noopener noreferrer" style="color: #6b7280;">Personvernerklæring</a> • 
                <a href="https://aktivlogg.no/betingelser/" style="color: #6b7280;">Salgs- og leveringsbetingelser</a>
              </p>
            </div>
          </div>
        </body>
        </html>
      `);
      previewWindow.document.close();
    } else {
      toast.error('Kunne ikke åpne forhåndsvisning. Tillat popup-vinduer i nettleseren.');
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6 text-white">
        <div className="text-center">Laster email-administrasjon...</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-6 text-white">
      {/* Tab Navigation */}
      <div className="border-b border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-yellow-500 text-yellow-500'
                : 'border-transparent text-gray-400 hover:text-white hover:border-gray-300'
            }`}
          >
            📧 E-post Maler
          </button>
          <button
            onClick={() => setActiveTab('ses-status')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'ses-status'
                ? 'border-yellow-500 text-yellow-500'
                : 'border-transparent text-gray-400 hover:text-white hover:border-gray-300'
            }`}
          >
            ⚙️ AWS SES Status
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'templates' && (
        <div>
          <h3 className="text-white font-medium mb-4 flex items-center">
            📧 E-post Maler
          </h3>
          
          <div className="space-y-4">
            {emailTemplates.map((template, index) => (
              <div key={template.id || index} className="bg-gray-700 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="text-white font-medium">{String(template.name || 'Ukjent mal')}</h4>
                    <div className="mb-2">
                      <span className="text-gray-400 text-sm">Emne:</span>
                      <span className="text-white ml-2">{String(template.subject || '')}</span>
                    </div>
                    <div className="text-gray-200 mt-1">
                      {renderTemplatePreview(template.content)}
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handlePreviewTemplate(template)}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                      title="Forhåndsvis e-post"
                    >
                      👁️ Forhåndsvis
                    </button>
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                    >
                      ✏️ Rediger
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'ses-status' && (
        <div>
          <h3 className="text-white font-medium mb-4">⚙️ AWS SES Status</h3>
          {sesStatus ? (
            <div className="bg-gray-700 rounded-lg p-4 space-y-3">
              <div>
                <span className="text-gray-400">Status:</span>
                <span className="text-green-400 ml-2 font-bold">Aktiv</span>
              </div>
              <div>
                <span className="text-gray-400">Maksimum 24t:</span>
                <span className="text-white ml-2">{String(sesStatus.max_24_hour_send || 0)}</span>
              </div>
              <div>
                <span className="text-gray-400">Sendt i dag:</span>
                <span className="text-white ml-2">{String(sesStatus.sent_last_24_hours || 0)}</span>
              </div>
              <div>
                <span className="text-gray-400">Send rate:</span>
                <span className="text-white ml-2">{String(sesStatus.max_send_rate || 0)}/sek</span>
              </div>
            </div>
          ) : (
            <div className="text-gray-400">SES status ikke tilgjengelig</div>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-white mb-4">
              Rediger Email Mal: {String(editingTemplate.name || '')}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-white text-sm font-medium mb-2">Emne</label>
                <input
                  type="text"
                  value={String(editingTemplate.subject || '')}
                  onChange={(e) => setEditingTemplate(prev => ({...prev, subject: e.target.value}))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                />
              </div>
              
              <div>
                <label className="block text-white text-sm font-medium mb-2">Innhold</label>
                <textarea
                  value={String(editingTemplate.content || '')}
                  onChange={(e) => setEditingTemplate(prev => ({...prev, content: e.target.value}))}
                  rows="15"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white font-mono text-sm"
                  placeholder="E-post innhold..."
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setEditingTemplate(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Avbryt
              </button>
              <button
                onClick={handleSaveTemplate}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Lagre
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailAdmin;